/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Image } from '@fluentui/react/lib/Image';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { AudioPlayer } from 'src/core/components/AudioPlayer';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { CallTranscriptionContent } from 'src/languageStudio/appShell/prebuiltDemos/callCenterDemo/CallTranscriptionContent';
import { getDuration, getSentiment } from 'src/languageStudio/appShell/prebuiltDemos/utils/callCenterUtils';
import { CallCenterDemoSample } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import { AnalyzedConversationViewer } from 'src/packages/analyzed-conversation-viewer/lib/esm';

type CallCenterResultsProps = { selectedSample: CallCenterDemoSample };

const messages = defineMessages({
    form: { id: 'languageStudio.callCenterResults.form', defaultMessage: 'this form' },
    preview: { id: 'languageStudio.callCenterResults.preview', defaultMessage: 'PREVIEW' },
    callSummary: { id: 'languageStudio.callCenterResults.callSummary', defaultMessage: 'Call summary' },
    issueSummary: { id: 'languageStudio.callCenterResults.issueSummary', defaultMessage: 'Issue summary' },
    analyzeSentence: { id: 'languageStudio.callCenterResults.analyzeSentence', defaultMessage: 'Analyze sentences' },
    resolutionSummary: { id: 'languageStudio.callCenterResults.resolutionSummary', defaultMessage: 'Resolution summary' },
    callTranscript: { id: 'languageStudio.callCenterResults.callTranscript', defaultMessage: 'Original call transcript' },
    callTranscriptSectionTitle: {
        id: 'languageStudio.callCenterResults.callTranscriptSectionTitle',
        defaultMessage: 'Conversation transcript'
    },
    callTranscriptionSectionTextDesc: {
        id: 'languageStudio.callCenterResults.callTranscriptionSectionTextDesc',
        defaultMessage: 'Showing original uploaded text file (.txt).'
    },
    callTranscriptionAudioLearnMore: {
        id: 'languageStudio.callCenterResults.callTranscriptionAudioLearnMore',
        defaultMessage: 'Learn more about conversation transcription'
    },
    analyzedSectionDesc: {
        id: 'languageStudio.callCenterResults.analyzedSectionDesc',
        defaultMessage: `Sample audio was transcribed and analyzed to identify sensitive personally identifiable information (PII) into
        pre-defined classes using Conversation PII (Preview) and sentence level sentiment scores.`
    },
    analyzedSectionAudioDesc: {
        id: 'languageStudio.callCenterResults.analyzedSectionAudioDesc',
        defaultMessage: `Sample audio was transcribed and analyzed to identify sensitive personally identifiable information (PII) into pre-defined classes using Conversation PII **Preview** and sentence level sentiment scores.`
    },
    callSummarySectionDesc: {
        id: 'languageStudio.callCenterResults.callSummarySectionDesc',
        defaultMessage: `The conversation summarization generates a summary of customer issue and solutions tried in a customer-and-agent call/chat.`
    },
    callSummaryAudioSectionDesc: {
        id: 'languageStudio.callCenterResults.callSummaryAudioSectionDesc',
        defaultMessage: `The conversation summarization **Preview** generates a summary of **issues** and **resolutions** from a two-party conversation, especially between a customer and an agent. This is a **gated** public preview service, and to request access please fill out`
    },
    callTranscriptionAudioDesc: {
        id: 'languageStudio.callCenterResults.callTranscriptionAudioDesc',
        defaultMessage: `Conversation transcription is a speech-to-text solution that provides real-time or asynchronous
        transcription of any conversation which was used as input to text analytics API in this sample. Channel
        diarization is used for speaker separation for stereo audio and diarization analysis is used for mono
        channel audio.`
    }
});

const theme = getScopedTheme('CallCenterDemoPage');
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { border: `1px solid ${theme.borderColor}`, height: '450px' }
});

export const CallCenterResults = (props: CallCenterResultsProps) => {
    const { selectedSample } = props;
    const { localize } = useLocalization();

    const callSummary = selectedSample?.output.conversationAnalyticsResults.conversationSummaryResults.conversations[0];
    const sentences =
        selectedSample?.output.transcription.recognizedPhrases.map((phrase, phraseIndex) => ({
            text: phrase.nBest[0].display,
            duration: getDuration(phrase.duration),
            speakerTitle: `Speaker ${phrase.channel}`,
            sentiment: getSentiment(phrase.nBest[0].sentiment),
            entities:
                selectedSample?.output.conversationAnalyticsResults.conversationPiiResults.conversations[0].conversationItems[phraseIndex]
                    .entities,
            redactedText:
                selectedSample?.output.conversationAnalyticsResults.conversationPiiResults.conversations[0].conversationItems[phraseIndex]
                    .redactedContent.text
        })) ?? [];

    const getAnalyzedSectionAudioDesc = () => {
        const tokens = localize(messages.analyzedSectionAudioDesc).split('**');
        return (
            <LuisText>
                {tokens[0]}
                <LuisText fontWeight={'bold'}>{tokens[1]}</LuisText>
                {tokens[2]}
            </LuisText>
        );
    };

    return (
        <Pivot linkSize="large" styles={{ root: { flexWrap: 'wrap', display: 'flex' } }}>
            <PivotItem headerText={localize(messages.analyzeSentence)} style={{ paddingTop: 12 }}>
                <Stack tokens={{ childrenGap: 12 }}>
                    <LuisText>
                        {selectedSample?.sampleType === 'audio' ? getAnalyzedSectionAudioDesc() : localize(messages.analyzedSectionDesc)}
                    </LuisText>
                    {selectedSample?.sampleType === 'audio' && <AudioPlayer audioSrc={selectedSample.audioLink} />}
                    <AnalyzedConversationViewer result={{ sentences }} />
                </Stack>
            </PivotItem>

            <PivotItem
                style={{ paddingTop: 12 }}
                headerText={localize(messages.callSummary)}
                onRenderItemLink={() => (
                    <Stack horizontal>
                        <LuisText styles={{ root: { font: 'inherit' } }}>{localize(messages.callSummary)}</LuisText>&nbsp;
                    </Stack>
                )}>
                <Stack tokens={{ childrenGap: 12 }}>
                    <LuisText>
                        {localize(messages.callSummarySectionDesc)}
                        &nbsp;
                    </LuisText>
                    {selectedSample?.sampleType === 'audio' && <AudioPlayer audioSrc={selectedSample.audioLink} />}
                    <Stack styles={containerStyles} tokens={{ childrenGap: 20, padding: '25px 20px' }}>
                        {!selectedSample ? (
                            <Stack grow horizontalAlign="center" verticalAlign="center">
                                <Image src={getAssetsUrl(`assets/images/customText/newEmptyState.svg`)} />
                            </Stack>
                        ) : (
                            [
                                <Stack tokens={{ childrenGap: 12 }}>
                                    <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                                        {localize(messages.issueSummary)}
                                    </LuisText>
                                    <LuisText styles={{ root: { whiteSpace: 'pre-wrap' } }}>
                                        {callSummary.summaries.find(s => s.aspect === 'issue').text}
                                    </LuisText>
                                </Stack>,
                                <Stack tokens={{ childrenGap: 12 }}>
                                    <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                                        {localize(messages.resolutionSummary)}
                                    </LuisText>
                                    <LuisText styles={{ root: { whiteSpace: 'pre-wrap' } }}>
                                        {callSummary.summaries.find(s => s.aspect === 'resolution').text}
                                    </LuisText>
                                </Stack>
                            ]
                        )}
                    </Stack>
                </Stack>
            </PivotItem>

            <PivotItem style={{ paddingTop: 12 }} headerText={localize(messages.callTranscript)}>
                <CallTranscriptionContent selectedSample={selectedSample} />
            </PivotItem>
        </Pivot>
    );
};
